import React, { useLayoutEffect, useRef, useState } from 'react';
import { SectionHeader } from '@charitystack/shared';
import DetailLearnMore from './DetailLearnMore';

/** How many lines of About show before "Learn more" (owner call 2026-10-23). */
export const ABOUT_CLAMP_LINES = 4;

/**
 * The overlay's About section, both layouts (owner call 2026-10-23): the description shows its
 * first ABOUT_CLAMP_LINES lines, fading out when there is more, and DetailLearnMore sits right
 * under it, the same link the embed's detail page ends on:
 * - clipped -> "Learn more" opens a sheet with the full description and the FAQs;
 * - fits -> "FAQs" opens the FAQs sheet (when there are any);
 * - no description -> just the FAQs link, when there are FAQs.
 * The sheet covers the nearest [data-pane-sheet-scope], which must be `position: relative` and
 * clip its overflow (usePaneSheet).
 *
 * `descriptionClassName` is the layout's prose recipe and `lineHeightVar` the line-height token it
 * uses, so the clip lands on a line boundary.
 */
export default function ClampedAbout({
  html,
  descriptionClassName,
  lineHeightVar,
  title,
  faqs,
  elementID,
  lines = ABOUT_CLAMP_LINES,
}) {
  const textRef = useRef(null);
  const [isClipped, setIsClipped] = useState(false);

  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el) return undefined;
    const measure = () => setIsClipped(el.scrollHeight > el.clientHeight + 1);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    // Watch the content inside the clipped box as well: an image or a web font arriving late
    // grows the text but not the box, and the box alone would never report it.
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    if (el.firstElementChild) observer.observe(el.firstElementChild);
    return () => observer.disconnect();
  }, [html]);

  const learnMore = (
    <DetailLearnMore
      title={title}
      descriptionHtml={html}
      isDescriptionClipped={Boolean(html) && isClipped}
      faqs={faqs}
      elementID={elementID}
    />
  );

  if (!html) return <div className="empty:hidden">{learnMore}</div>;

  return (
    <div className="flex flex-col gap-[var(--cs-space-3)]">
      <SectionHeader>About</SectionHeader>
      <div className="flex flex-col items-start gap-[var(--cs-space-2)]">
        <div
          ref={textRef}
          data-clipped={isClipped || undefined}
          className="w-full overflow-hidden data-[clipped]:[mask-image:linear-gradient(to_bottom,black_calc(100%-var(--cs-space-6)),transparent)]"
          // cs-allow-hardcoded: a line count times the layout's own line-height token, not a size.
          style={{ maxHeight: `calc(${lines} * var(${lineHeightVar}))` }}
        >
          {/* The prose recipe sits on the wrapper that holds the blocks, so its spacing between
              paragraphs, lists and images still applies. */}
          <div className={descriptionClassName} dangerouslySetInnerHTML={{ __html: html }} />
        </div>
        {learnMore}
      </div>
    </div>
  );
}
